<template>
    <div
        class="station-list"
        :class="{
            'closed': closedRef,
            'open': openRef,
        }"
    >
        <button
            class="toggle"
            @click="() => { closedRef = !closedRef; openRef = !openRef }"
        >
            <DSIcon size="xs" code="chevron-left" />
            <DSIcon size="xs" code="list-view" />
            <span class="text">{{ $t('stationList') }}</span>
        </button>
        <div class="list">
            <DSHeightTransition
                class="single-station"
                :override-overflow="true"
                :open="Object.keys(selectedStation).length > 0"
            >
                <div>
                    <button
                        class="header back"
                        @click="selectedStation = {}"
                    >
                        <DSIcon code="chevron-left" size="s" />
                        <span>{{ $t('back') }}</span>
                    </button>
                    <div class="content">
                        <h3 class="title h3">{{ selectedStation.name }}</h3>
                        <p v-if="selectedStation.description">{{ selectedStation.description }}</p>
                        <p
                            v-if="selectedStation.street"
                            class="address"
                        >
                            <span v-if="selectedStation.street">{{ selectedStation.street }}</span>
                            <span v-if="selectedStation.postcode && selectedStation.municipality">{{ `${selectedStation.postcode} ${selectedStation.municipality}` }}</span>
                        </p>
                        <template v-if="selectedStation.storeOpeningHours">
                            <hr />
                            <h4 class="h5">{{ $t('openingHours') }}</h4>
                            <BlocksOpeningHours
                                v-if="computeOpenHours"
                                :name="$t('opens')"
                                icon="clock"
                                :opening-hours="computeOpenHours"
                            />
                        </template>
                        <template v-if="selectedStation.fuels?.length > 0">
                            <hr />
                            <h4 class="h5">{{ $t('fuelsHeading') }}</h4>
                            <div class="fuels">
                                <span
                                    v-for="item, key in selectedStation.fuels"
                                    :key="`fuel-${key}`"
                                >{{ $t(`stationAttributes.${item}`) }}</span>
                            </div>
                        </template>
                        <template v-if="selectedStation.carWashSize">
                            <hr />
                            <h4 class="h5">{{ $t('carwash.heading') }}</h4>
                            <div class="carwash-dimensions">
                                <span
                                    v-for="item, key in selectedStation.carWashSize.split('/')"
                                    :key="`dimension-${key}`"
                                >{{ $t(`carwash.dimension-${key}`, { cm: item }) }}</span>
                            </div>
                        </template>
                    </div>
                </div>
            </DSHeightTransition>
            <DSHeightTransition
                :override-overflow="true"
                :open="Object.keys(selectedStation).length === 0"
            >
                <template v-for="place, key in listResources">
                    <button
                        v-if="place.placeId"
                        :key="`city-${place.placeId}`"
                        class="station"
                        @click="setCity(place.placeId)"
                    >
                        <DSIcon code="city" size="xxxl" />
                        <div class="text">
                            <h4 class="title">{{ place.title }}</h4>
                        </div>
                    </button>
                    <button
                        v-else
                        :key="`station-${place.id}-${key}`"
                        class="station"
                        :class="{
                            'selected': selectedStation.id === place.id,
                        }"
                        v-on="stationLink(place)"
                    >
                        <img
                            class="logo"
                            :src="resolveBrand(place.brand)"
                        />
                        <div class="text">
                            <h4 class="title">{{ place.name }}</h4>
                            <span class="description">{{ place.description }}</span>
                            <span class="open-hours">{{ $locator.getTodayOpeningHours(place.storeOpeningHours, i18n) }}</span>
                        </div>
                        <nuxt-link
                            :href="directionLink(place)"
                            class="direction-icon"
                            @click="e => directionLinkClick(e, place.title)"
                        >
                            <DSIcon
                                code="direction"
                                size="xl"
                            />
                        </nuxt-link>
                    </button>
                </template>
            </DSHeightTransition>
        </div>
        <div class="mobile-background" @click="() => openRef = !openRef">
            <button class="close">
                <DSIcon code="chevron-right" />
            </button>
        </div>
    </div>
</template>
<script setup>
import { ref, onMounted, computed, watch, inject } from 'vue';
import { useNuxtApp, useI18n, useRouter, useRuntimeConfig } from '#imports';
import { DateTime } from 'luxon';
import { sortBy } from 'lodash-es';

const router = useRouter();
const { $gtm, $locator } = useNuxtApp();
const closedRef = ref(false);
const openRef = ref(false);
const { stationPages } = { ...useRuntimeConfig().public.locator };

const emit = defineEmits(['toggleOpen', 'selectStation', 'setMap']);
const selectedStation = inject('selectedStation');

const props = defineProps({
    closed: {
        type: Boolean,
        required: false,
        default: false,
    },
    cities: {
        type: Array,
        required: false,
        default: () => [],
    },
    stations: {
        type: Array,
        required: false,
        default: () => [],
    },
});

watch(() => openRef.value, value => {
    emit('toggleOpen', !value);
});


const i18n = useI18n({
    useScope: 'global',
    messages: {
        'en-EU': {
            stationList: 'Station list',
            open24h: 'Open 24h',
        },
        'fi-FI': {
            stationList: 'Asemalista',
            open24h: 'Avoinna 24h',
            helmisimpukka: 'Helmisimpukka',
            fuelsHeading: 'Polttoaineet',
            carwash: {
                'heading': 'Perfect Autopesu',
                'dimension-0': 'Pesukoneen korkeus {cm} cm',
                'dimension-1': 'Pesukoneen leveys {cm} cm',
            },
        },
        'sv-SE': {
            open24h: 'Öppen 24h',
        },
        'no-NO': {
            open24h: 'Åpen 24 timer',
        },
    },
});

const listResources = computed(() => [
    ...props.cities,
    ...props.stations,
]);

const computeOpenHours = computed(() => {
    const open24h = selectedStation.value.storeOpeningHours?.filter(item =>
        item?.openHours?.[0]?.startTime === '00:00:00'
        && item?.openHours?.[0]?.endTime === '24:00:00')?.length === 8
        || false;

    if (open24h) {
        return [{
            from: i18n.t('days.Mon'),
            to: i18n.t('days.Sun'),
            hours: [{
                startTime: '00:00:00',
                endTime: '24:00:00',
            }],
        }];
    }

    const storeOpeningHours = [...selectedStation.value.storeOpeningHours];

    storeOpeningHours.forEach((value, key) => {
        storeOpeningHours[key].day = Number(DateTime.fromString(value.date, 'yyyy-MM-dd').toFormat('c'));
    });

    const sortedOpenHours = sortBy(storeOpeningHours, ['day']);

    const openingHoursTable = [];
    let i = 0;

    sortedOpenHours.forEach((value, key) => {
        if (!openingHoursTable[i]) {
            openingHoursTable[i] = {};
        }
        if (!openingHoursTable[i].from) {
            openingHoursTable[i].from = i18n.t(`days.${DateTime.fromString(value.date, 'yyyy-MM-dd').toFormat('ccc')}`);
        }
        openingHoursTable[i].hours = value.openHours;

        if (!sortedOpenHours[key + 1] || JSON.stringify(value.openHours) !== JSON.stringify(sortedOpenHours[key + 1].openHours)) {
            openingHoursTable[i].to = i18n.t(`days.${DateTime.fromString(value.date, 'yyyy-MM-dd').toFormat('ccc')}`);
            i++;
        }
    });

    return openingHoursTable;
});

onMounted(() => {
    closedRef.value = props.closed;
});

const stationLink = station => ({
    click: () => {
        if (stationPages) {
            router.push(`/${station.path}`);
        } else {
            selectedStation.value = station;
            emit('selectStation', station);
        }
    },
});

const directionLink = station => {
    const { lat, lon } = { ...station.location };

    if (lat && lon) {
        return `https://maps.google.com/?daddr=${lat},${lon}`;
    }

    return null;
};

const setCity = async placeId => {
    try {
        const geocoder = await new google.maps.Geocoder();
        const { results } = await geocoder.geocode({ placeId });
        const { 0: selection } = results;

        emit('setMap', {
            lat: selection.geometry.location.lat(),
            lng: selection.geometry.location.lng(),
            zoom: 11,
        });
    } catch (e) {
        throw new Error('Geocoder lookup failed');
    }
};

const directionLinkClick = (event, name) => {
    event.stopPropagation();

    if ($gtm) {
        $gtm.push({
            content_type: 'directions',
            link_label: name,
            outbound: 'true',
            event: 'click',
        });
    }
};

const resolveBrand = brand => {
    const lcBrand = brand.toLowerCase();
    if (lcBrand === 'ploq') {
        return '/images/ploq-logo.png';
    } else if (lcBrand === 'shell') {
        return '/images/shell-logo.png';
    }

    return '/images/st1-logo.webp';
};
</script>
<style scoped>
.station-list {
  position: relative;
  width: 100%;
  pointer-events: none;
}
@media (min-width: 1200px) {
  .station-list {
    max-width: 24rem;
  }
  .station-list.closed .list {
    transform: translateX(100%) scaleX(0);
  }
  .station-list.closed .toggle .icon-chevron-left {
    transform: scaleX(-1);
  }
}
@media (max-width: 1199px) {
  .station-list.open .mobile-background {
    opacity: 1;
    pointer-events: auto;
  }
  .station-list.open .list {
    width: calc(100% - 4rem);
    transform: translateX(0%) scaleX(1);
    box-shadow: -5px 0 5px rgba(0, 0, 0, 0.2);
  }
}

.station {
  transition: all 0.2s ease-in-out;
  display: grid;
  grid-template-columns: 40px 1fr 1.75rem;
  grid-gap: 0 1rem;
  padding: 1rem;
  border: 1px solid var(--color-base-grey-200);
  background: var(--color-base-white);
  text-align: left;
  align-self: start;
  width: 100%;
  backface-visibility: hidden;
}
.station .logo {
  align-self: center;
  justify-self: center;
}
.station .text {
  display: flex;
  flex-flow: column wrap;
  align-self: center;
}
.station .title {
  transition: all 0.2s ease-in-out;
  grid-column-start: 2;
  font-size: 1rem;
}
.station .open-hours {
  font-size: 0.875rem;
  font-weight: 700;
  color: var(--color-primary-500);
}
.station.selected {
  border: 1px solid var(--color-primary-500);
}
.station:not(:last-child) {
  margin-bottom: 1rem;
}
.station:hover {
  cursor: pointer;
  border: 1px solid var(--color-primary-500);
}
.station:hover .title {
  color: var(--color-primary-500);
}

.direction-icon {
  transition: all 0.2s ease-in-out;
  align-self: center;
}
.direction-icon:hover {
  color: var(--color-primary-500);
}

.toggle {
  z-index: 3;
  position: absolute;
  right: 0;
  top: 6rem;
  border: 1px solid var(--color-base-grey-400);
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  pointer-events: auto;
  background: var(--color-base-white);
  transform: translateX(50%);
  -webkit-tap-highlight-color: transparent;
}
.toggle .icon {
  transition: all 0.2s ease-in-out;
  transform-origin: center center;
}
.toggle .text {
  font-size: 0.875rem;
  font-weight: 800;
}
@media (min-width: 1200px) {
  .toggle .icon-list-view, .toggle .text {
    display: none;
  }
}
@media (max-width: 1199px) {
  .toggle {
    top: 13.5rem;
    right: 1.5rem;
    width: auto;
    height: auto;
    z-index: 2;
    transform: translateX(0%);
    padding: 0.5rem 0.75rem;
    border-radius: 0.25rem;
  }
  .toggle .icon-list-view {
    margin-right: 0.5rem;
  }
  .toggle .icon-chevron-left {
    display: none;
  }
}

.mobile-background {
  transition: all 0.2s ease-in-out;
  position: relative;
  background-color: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(0.25rem);
  width: 100%;
  height: 100%;
  z-index: 3;
  pointer-events: none;
  opacity: 0;
}
@media (min-width: 1200px) {
  .mobile-background {
    display: none;
  }
}
.mobile-background .close {
  margin-top: 1rem;
  margin-left: 1rem;
  width: 2rem;
  height: 2rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  pointer-events: none;
  background: var(--color-base-white);
}

.list {
  display: grid;
  transition: all 0.5s ease;
  z-index: 2;
  position: absolute;
  box-shadow: 5px 0 5px rgba(0, 0, 0, 0.2);
  width: 100%;
  height: 100%;
  padding: 1.5rem 1.2rem 1.5rem 1.5rem;
  background-color: var(--color-base-white);
  pointer-events: auto;
  right: 0;
  transform-origin: left center;
  transform: translateX(100%);
  align-content: start;
  max-height: 100%;
  overflow-x: hidden;
  overflow-y: scroll;
}
@media (max-width: 1199px) {
  .list {
    z-index: 4;
    width: calc(100% - 4rem);
  }
}
@media (min-width: 1200px) {
  .list {
    background-image: linear-gradient(to right, rgba(0, 0, 0, 0.2) 0%, transparent 2%, transparent 100%);
  }
}

.single-station {
  margin: -1.5rem;
}
.single-station .header.back {
  border-bottom: 1px solid var(--color-base-grey-200);
  width: 100%;
  display: flex;
  align-items: center;
  padding: 0.75rem 1.5rem;
}
.single-station .header.back .icon {
  margin-right: 0.5rem;
}
.single-station hr {
  margin: 1.5rem -1.5rem;
}
.single-station .content {
  padding: 1.5rem;
}
.single-station .address {
  display: flex;
  flex-flow: column wrap;
}
.single-station .opening-hours {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
}
.single-station .opening-hours .hours {
  width: 100%;
  padding: 0 0 0 0.5rem;
  text-align: right;
}
.single-station .opening-hours > *:nth-child(4n), .single-station .opening-hours > *:nth-child(4n-1) {
  background: var(--color-base-grey-100);
}
.single-station .fuels {
  display: flex;
  flex-flow: column wrap;
}
.single-station .carwash-dimensions {
  display: flex;
  flex-flow: column wrap;
}
.single-station .open-hours :deep(.icon.icon-clock), .single-station .open-hours :deep(.block > strong:first-child) {
  display: none;
}

.fade-vertical-leave-active {
  transition: opacity 0.3s ease-out, transform 0.8s ease-out;
}

.fade-vertical-enter-active {
  transition: 0.2s opacity 0.5s ease-out, transform 1s cubic-bezier(0.34, 0.74, 0.5, 1);
}

.fade-vertical-enter-to, .fade-vertical-leave-from {
  transform: scale(1) translateY(0);
}

.fade-vertical-enter-from {
  position: absolute;
  z-index: 1;
  transform: translateY(-500%);
  opacity: 0;
}

.fade-vertical-leave-to {
  position: absolute;
  margin-left: 1.5rem;
  width: calc(100% - 3rem);
  z-index: 0;
  transform: translateY(500%);
  opacity: 0;
}
</style>
